/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../logging/Log";

/**
 * Circuit breaker states (REQ-ERR-07).
 */
export enum CircuitBreakerState {
  /** Normal operation — connections allowed. */
  CLOSED = "CLOSED",
  /** Too many consecutive failures — reject all attempts for cooldown period. */
  OPEN = "OPEN",
  /** Cooldown expired — allow exactly one probe attempt. */
  HALF_OPEN = "HALF_OPEN"
}

/**
 * WebSocket circuit breaker implementation (REQ-ERR-07).
 *
 * Prevents rapid reconnection hammering during sustained server outages.
 *
 * - CLOSED: Normal. Tracks consecutive failures.
 * - OPEN: After 5 consecutive failures, reject all attempts for ≥60s.
 * - HALF_OPEN: After cooldown, allow one probe. Success → CLOSED. Failure → OPEN.
 *
 * The circuit breaker does NOT prevent the reconnect timer from firing —
 * it prevents the actual connect flow from executing. When the circuit is OPEN,
 * `scheduleReconnect()` is still allowed (to maintain backoff progression),
 * but `allowAttempt()` returns false so the connect flow is skipped.
 */
export class WebSocketCircuitBreaker {
  private static readonly TAG: string = "WebSocketCircuitBreaker";

  /** Number of consecutive failures before opening the circuit (REQ-ERR-07). */
  private static readonly FAILURE_THRESHOLD: number = 5;

  /** Minimum cooldown in milliseconds while circuit is OPEN (REQ-ERR-07: ≥60s). */
  private static readonly COOLDOWN_MS: number = 60_000;

  private mState: CircuitBreakerState = CircuitBreakerState.CLOSED;
  private mConsecutiveFailures: number = 0;
  private mOpenedAt: number = 0;

  /**
   * Record a successful connection. Resets the circuit to CLOSED.
   */
  public recordSuccess(): void {
    if (this.mState !== CircuitBreakerState.CLOSED) {
      Log.d(
        WebSocketCircuitBreaker.TAG,
        `recordSuccess(): ${this.mState} → CLOSED (reset after successful connection)`
      );
    }
    this.mState = CircuitBreakerState.CLOSED;
    this.mConsecutiveFailures = 0;
  }

  /**
   * Record a failed connection attempt. May trip the circuit to OPEN.
   */
  public recordFailure(): void {
    this.mConsecutiveFailures++;

    if (this.mState === CircuitBreakerState.HALF_OPEN) {
      // Probe failed — re-open the circuit
      this.mState = CircuitBreakerState.OPEN;
      this.mOpenedAt = Date.now();
      Log.d(
        WebSocketCircuitBreaker.TAG,
        `recordFailure(): HALF_OPEN → OPEN (probe failed, failures=${this.mConsecutiveFailures})`
      );
      return;
    }

    if (
      this.mConsecutiveFailures >= WebSocketCircuitBreaker.FAILURE_THRESHOLD &&
      this.mState === CircuitBreakerState.CLOSED
    ) {
      this.mState = CircuitBreakerState.OPEN;
      this.mOpenedAt = Date.now();
      Log.w(
        WebSocketCircuitBreaker.TAG,
        `recordFailure(): CLOSED → OPEN (${this.mConsecutiveFailures} consecutive failures)`
      );
    } else {
      Log.d(
        WebSocketCircuitBreaker.TAG,
        `recordFailure(): state=${this.mState}, failures=${this.mConsecutiveFailures}/${WebSocketCircuitBreaker.FAILURE_THRESHOLD}`
      );
    }
  }

  /**
   * Check whether a connection attempt is allowed under the current circuit state.
   *
   * - CLOSED: always allowed.
   * - OPEN: allowed only after cooldown expires (transitions to HALF_OPEN).
   * - HALF_OPEN: allowed (exactly one probe).
   *
   * @returns true if the attempt may proceed
   */
  public allowAttempt(): boolean {
    switch (this.mState) {
      case CircuitBreakerState.CLOSED:
        return true;

      case CircuitBreakerState.OPEN: {
        const elapsed: number = Date.now() - this.mOpenedAt;
        if (elapsed >= WebSocketCircuitBreaker.COOLDOWN_MS) {
          this.mState = CircuitBreakerState.HALF_OPEN;
          Log.d(
            WebSocketCircuitBreaker.TAG,
            `allowAttempt(): OPEN → HALF_OPEN (cooldown ${Math.round(elapsed / 1000)}s elapsed)`
          );
          return true;
        }
        Log.d(
          WebSocketCircuitBreaker.TAG,
          `allowAttempt(): OPEN — rejected (${Math.round((WebSocketCircuitBreaker.COOLDOWN_MS - elapsed) / 1000)}s remaining)`
        );
        return false;
      }

      case CircuitBreakerState.HALF_OPEN:
        return true;
    }
  }

  /**
   * Get the current circuit breaker state.
   */
  public getState(): CircuitBreakerState {
    return this.mState;
  }

  /**
   * Get the number of consecutive failures.
   */
  public getConsecutiveFailures(): number {
    return this.mConsecutiveFailures;
  }

  /**
   * Reset the circuit breaker to initial CLOSED state.
   */
  public reset(): void {
    this.mState = CircuitBreakerState.CLOSED;
    this.mConsecutiveFailures = 0;
    this.mOpenedAt = 0;
  }
}
